import { generateUuid } from "../../utils/id";
import type { Vector2, Vector3, Vector5, Vector6 } from "../../types/scene";
import {
  AMBIGUOUS_GEOM_ROLE_WARNING_PREFIX,
  shouldEmitSyntheticVisualForMeshCollisionAttrs,
} from "./geomRole";
import { normalizeQuat, parseElementOrientation } from "./orientation";
import { parseSolimp, parseSolref } from "./attributeUtils";
import type { AmbiguousGeomRoleCandidate, ParsedGeom } from "./types";

type GeomDefaultProperty =
  | "pos"
  | "quat"
  | "size"
  | "fromto"
  | "type"
  | "mesh"
  | "hfield"
  | "material"
  | "mass"
  | "density"
  | "friction"
  | "solref"
  | "solimp"
  | "contype"
  | "conaffinity"
  | "condim"
  | "group";

type BodyGeomParser = {
  eulerseq: string;
  angleUnit: "radian" | "degree";
  classParentMap: Record<string, string>;
  collisionTypeMap: Record<string, string>;
  geomClassDefaults: Record<string, Partial<Record<GeomDefaultProperty, unknown>>>;
  globalVisualCounter: number;
  globalGeomCounter: number;
  ambiguousGeomRoleCandidates: AmbiguousGeomRoleCandidate[];
  warn: (message: string) => void;
};

export const resolveChildClass = (bodyElem: Element): string | null => {
  let el: Element | null = bodyElem;
  while (el && el.tagName.toLowerCase() === "body") {
    const cc = el.getAttribute("childclass");
    if (cc) return cc;
    el = el.parentElement;
  }
  return null;
};

export const getInheritedCollisionType = (
  parser: BodyGeomParser,
  className: string
): string | null => {
  let cur: string | null = className;
  const visited = new Set<string>();
  while (cur && !visited.has(cur)) {
    visited.add(cur);
    if (parser.collisionTypeMap && parser.collisionTypeMap[cur]) {
      return parser.collisionTypeMap[cur];
    }
    cur = parser.classParentMap[cur] || null;
  }
  return null;
};

export const getInheritedGeomProp = (
  parser: BodyGeomParser,
  className: string | null,
  prop: GeomDefaultProperty
): unknown => {
  let cur: string | null = className;
  const visited = new Set<string>();
  while (cur !== null && !visited.has(cur)) {
    visited.add(cur);
    const defaults = parser.geomClassDefaults[cur];
    if (defaults && defaults[prop] !== undefined) {
      return defaults[prop];
    }
    cur = parser.classParentMap[cur] || null;
  }
  if (!visited.has("")) {
    const rootDefaults = parser.geomClassDefaults[""];
    if (rootDefaults && rootDefaults[prop] !== undefined) {
      return rootDefaults[prop];
    }
  }
  return undefined;
};

export const getResolvedGeomBitmask = (
  parser: BodyGeomParser,
  geomElem: Element,
  className: string | null,
  prop: "contype" | "conaffinity"
): number | undefined => {
  const explicit = geomElem.getAttribute(prop);
  if (explicit != null) {
    const parsed = Number(explicit);
    if (Number.isFinite(parsed)) return parsed;
  }
  const inherited = getInheritedGeomProp(parser, className, prop);
  if (typeof inherited === "number" && Number.isFinite(inherited)) return inherited;
  if (typeof inherited === "string") {
    const parsed = Number(inherited);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

export const isGeomVisualOnly = (
  parser: BodyGeomParser,
  geomElem: Element,
  className: string | null
): boolean => {
  const contype = getResolvedGeomBitmask(parser, geomElem, className, "contype");
  const conaffinity = getResolvedGeomBitmask(parser, geomElem, className, "conaffinity");

  const hasContactMask = contype !== undefined || conaffinity !== undefined;
  if (hasContactMask) {
    const hasAnyContact = (contype ?? 0) > 0 || (conaffinity ?? 0) > 0;
    return !hasAnyContact;
  }

  return false;
};

export const parseBodyGeoms = (
  rawParser: unknown,
  bodyElem: Element,
  name: string | null
): ParsedGeom[] => {
  const parser = rawParser as BodyGeomParser;
  const getInheritedProp = (className: string | null, prop: GeomDefaultProperty): unknown =>
    getInheritedGeomProp(parser, className, prop);
  const getGeomBitmask = (
    geomElem: Element,
    className: string | null,
    attr: "contype" | "conaffinity"
  ): number | undefined => getResolvedGeomBitmask(parser, geomElem, className, attr);
  const getVisualOnly = (geomElem: Element, className: string | null): boolean =>
    isGeomVisualOnly(parser, geomElem, className);
  const getCollisionType = (className: string): string | null =>
    getInheritedCollisionType(parser, className);
  // Parse geom elements in document order to ensure consistent naming
  const allGeoms = Array.from(bodyElem.querySelectorAll(":scope > geom") as NodeListOf<Element>);
  const parseNumericArray = (value: string | null | undefined): number[] | undefined => {
    if (!value) return undefined;
    const nums = value
      .trim()
      .split(/\s+/)
      .map(Number)
      .filter((n) => Number.isFinite(n));
    return nums.length ? nums : undefined;
  };
  const resolveGeomAttr = (
    g: Element,
    className: string | null,
    attr: GeomDefaultProperty
  ): unknown => {
    const explicit = g.getAttribute(attr);
    return explicit != null ? explicit : getInheritedProp(className, attr);
  };
  const getGeomClass = (g: Element): string | null =>
    g.getAttribute("class") || resolveChildClass(bodyElem) || null;
  const getGeomMesh = (g: Element, className: string | null): string | null => {
    const mesh = resolveGeomAttr(g, className, "mesh");
    return typeof mesh === "string" && mesh.trim() ? mesh.trim() : null;
  };
  const getGeomType = (g: Element, className: string | null, fallback = "sphere"): string => {
    const meshName = getGeomMesh(g, className);
    if (meshName) return "mesh";
    const type = resolveGeomAttr(g, className, "type");
    return String(type || fallback).toLowerCase();
  };
  const normalizeGeomSizeForType = (
    rawSize: number[] | undefined,
    type: string
  ): number[] | undefined => {
    if (!rawSize?.length) return undefined;
    const normalizedType = String(type || "").toLowerCase();
    switch (normalizedType) {
      case "sphere":
        return rawSize.slice(0, 1);
      case "capsule":
      case "cylinder":
        return rawSize.slice(0, Math.min(rawSize.length, 2));
      case "box":
      case "rectangle":
      case "ellipsoid":
      case "plane":
        return rawSize.slice(0, Math.min(rawSize.length, 3));
      case "hfield":
        return rawSize.slice(0, Math.min(rawSize.length, 4));
      default:
        return rawSize;
    }
  };
  const getGeomSize = (g: Element, className: string | null, type: string): number[] => {
    const explicitSize = parseNumericArray(g.getAttribute("size"));
    const inheritedSizeRaw = getInheritedProp(className, "size");
    const inheritedSize = Array.isArray(inheritedSizeRaw) ? inheritedSizeRaw : undefined;
    const size = normalizeGeomSizeForType(explicitSize || inheritedSize, type) || [];
    if (size.length) return size;
    switch (type) {
      case "plane":
        return [1, 1, 0.1];
      case "sphere":
        return [0.1];
      case "capsule":
      case "cylinder":
        return [0.1, 0.2];
      case "ellipsoid":
      case "box":
      default:
        return [0.1, 0.1, 0.1];
    }
  };
  const getGeomContactProps = (g: Element, className: string | null) => {
    const contactCategory = getGeomBitmask(g, className, "contype");
    const contactAffinity = getGeomBitmask(g, className, "conaffinity");
    const explicitCondim = g.getAttribute("condim");
    const parsedExplicitCondim = explicitCondim != null ? Number(explicitCondim) : undefined;
    const inheritedCondim = getInheritedProp(className, "condim");
    const contactDimensions = Number.isFinite(parsedExplicitCondim)
      ? parsedExplicitCondim
      : Number.isFinite(Number(inheritedCondim))
        ? Number(inheritedCondim)
        : undefined;
    return {
      contactCategory,
      contactAffinity,
      contactDimensions,
    };
  };
  const getGeomGroup = (g: Element, className: string | null): number | undefined => {
    const explicit = g.getAttribute("group");
    if (explicit != null) {
      const parsed = Number(explicit);
      if (Number.isFinite(parsed) && Number.isInteger(parsed)) return parsed;
    }
    const inherited = getInheritedProp(className, "group");
    const parsedInherited = Number(inherited);
    if (Number.isFinite(parsedInherited) && Number.isInteger(parsedInherited))
      return parsedInherited;
    return undefined;
  };
  const isRoleClass = (className: string | null): boolean => {
    const value = String(className || "")
      .trim()
      .toLowerCase();
    return value === "visual" || value === "collision" || value === "eef_collision";
  };
  const isCollisionLikeName = (value: string | null | undefined): boolean => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    if (!normalized) return false;
    return /(^|[_\-.])(col|coll|collision|collider|hull|convex|proxy)([_\-.]|$)/.test(normalized);
  };
  const hasDeclaredMaterial = (g: Element, className: string | null): boolean => {
    const explicitMaterial = g.getAttribute("material");
    const inheritedMaterial = getInheritedProp(className, "material");
    return (
      (typeof explicitMaterial === "string" && explicitMaterial.trim().length > 0) ||
      (typeof inheritedMaterial === "string" && inheritedMaterial.trim().length > 0)
    );
  };
  const hasSiblingVisualMesh = (geomElem: Element, meshName: string): boolean =>
    allGeoms.some((candidate) => {
      if (candidate === geomElem) return false;
      const candidateClass = getGeomClass(candidate);
      return (
        getGeomMesh(candidate, candidateClass) === meshName &&
        getVisualOnly(candidate, candidateClass)
      );
    });
  const warnAmbiguousMeshRole = (geomElem: Element, parsedGeom: ParsedGeom): void => {
    if (parsedGeom.visualOnly === true) return;
    const meshName = typeof parsedGeom.mesh === "string" ? parsedGeom.mesh.trim() : "";
    if (!meshName) return;
    const className = getGeomClass(geomElem);
    if (isRoleClass(className)) return;
    if (!hasDeclaredMaterial(geomElem, className)) return;
    if (hasSiblingVisualMesh(geomElem, meshName)) return;
    if (isCollisionLikeName(geomElem.getAttribute("name")) || isCollisionLikeName(meshName)) return;

    const bodyLabel = name || bodyElem.getAttribute("name") || "(unnamed body)";
    const geomLabel = geomElem.getAttribute("name") || "(unnamed geom)";
    const warning = `${AMBIGUOUS_GEOM_ROLE_WARNING_PREFIX} body "${bodyLabel}", geom "${geomLabel}", mesh "${meshName}" is effectively collidable but also has a material and no explicit visual/collision role. Defaulting to collision-only. Add separate visual/collision geoms, or set contype="0" conaffinity="0" for visual-only geometry.`;
    parser.ambiguousGeomRoleCandidates.push({
      warning,
      geomUuid: String(parsedGeom.uuid || ""),
      bodyName: bodyLabel,
      geomName: geomLabel,
      meshName,
    });
    parser.warn(warning);
  };
  const getFromTo = (g: Element): Vector6 | undefined => {
    const values = parseNumericArray(g.getAttribute("fromto"));
    return values && values.length >= 6 ? (values.slice(0, 6) as Vector6) : undefined;
  };
  // Process geoms in document order by filtering from allGeoms
  const meshGeoms: ParsedGeom[] = allGeoms
    .filter((g: Element) => getGeomMesh(g, getGeomClass(g)))
    .map((g: Element) => {
      const cls = getGeomClass(g);
      const meshName = getGeomMesh(g, cls);
      if (!meshName) return null;

      // Get position: explicit attribute > inherited from class > default [0,0,0]
      const explicitPos = g.getAttribute("pos")?.trim().split(/\s+/).map(Number);
      const inheritedPos = getInheritedProp(cls || null, "pos");
      const pos = explicitPos || inheritedPos || [0, 0, 0];

      // Orientation: explicit format > class-inherited quat > identity
      let quat: number[];
      const hasExplicitOrientation =
        g.hasAttribute("quat") ||
        g.hasAttribute("euler") ||
        g.hasAttribute("axisangle") ||
        g.hasAttribute("xyaxes") ||
        g.hasAttribute("zaxis");
      if (hasExplicitOrientation) {
        quat = normalizeQuat(
          parseElementOrientation(g, { eulerseq: parser.eulerseq, angleUnit: parser.angleUnit })
        );
      } else {
        const inheritedQuat = getInheritedProp(cls || null, "quat");
        const raw = inheritedQuat || [1, 0, 0, 0];
        const [w, x, y, z] = raw as number[];
        const mag = Math.sqrt(w * w + x * x + y * y + z * z);
        quat = mag > 0 ? [w / mag, x / mag, y / mag, z / mag] : [1, 0, 0, 0];
      }

      // Mesh geoms default to kind "mesh"; size not applicable -> default to [1,1,1]
      // Resolve material from geom attribute, or from default class (with inheritance), or fallback to default_mat
      let matName = g.getAttribute("material") || getInheritedProp(cls || null, "material");

      // Fallback to default_mat if still no material
      if (!matName) {
        matName = "default_mat";
      }

      const isVisualOnly = getVisualOnly(g, cls || null);
      const isCollision = !isVisualOnly;
      const contactProps = getGeomContactProps(g, cls || null);
      const group = getGeomGroup(g, cls || null);
      const explicitDensity = g.getAttribute("density");
      const inheritedDensity = getInheritedProp(cls, "density");
      const density =
        explicitDensity != null
          ? Number(explicitDensity)
          : inheritedDensity !== undefined
            ? Number(inheritedDensity)
            : undefined;

      const solverReference = (() => {
        const explicit = parseSolref(g.getAttribute("solref"));
        const inherited = parseSolref(getInheritedProp(cls || null, "solref"));
        return explicit || inherited || ([0.02, 1] as Vector2);
      })();

      const solverImpedance = (() => {
        const explicit = parseSolimp(g.getAttribute("solimp"));
        if (explicit) return explicit;
        const inherited = getInheritedProp(cls || null, "solimp");
        if (Array.isArray(inherited) && inherited.length >= 3) {
          return [
            Number(inherited[0] ?? 0.9),
            Number(inherited[1] ?? 0.95),
            Number(inherited[2] ?? 0.001),
            Number(inherited[3] ?? 0.5),
            Number(inherited[4] ?? 2),
          ] as Vector5;
        }
        return [0.9, 0.95, 0.001, 0.5, 2] as Vector5;
      })();

      const geom: ParsedGeom = {
        uuid: generateUuid(),
        name: g.getAttribute("name") || undefined,
        kind: "mesh",
        position: pos,
        orientation: quat,
        size: [1, 1, 1],
        mesh: meshName,
        materials: [matName],
        // Physics/Contact
        visualOnly: isVisualOnly,
        contactCategory: contactProps.contactCategory,
        contactAffinity: contactProps.contactAffinity,
        group,
        mjcfClass: g.getAttribute("class") || undefined,
        user: g.getAttribute("user") || undefined,
        density: Number.isFinite(density) ? density : undefined,
        solverReference: isCollision ? solverReference : undefined,
        solverImpedance: isCollision ? solverImpedance : undefined,
      };
      return geom;
    })
    .filter((geom): geom is ParsedGeom => geom !== null);

  // Parse visual geometries (non-mesh geoms classified as visual-only)
  // Process in document order by filtering from allGeoms
  const visualGeoms = allGeoms
    .filter((g: Element) => !getGeomMesh(g, getGeomClass(g)) && getVisualOnly(g, getGeomClass(g)))
    .map((g: Element) => {
      const className = getGeomClass(g);

      // Get position: explicit attribute > inherited from class > default [0,0,0]
      const explicitPos = g.getAttribute("pos")?.trim().split(/\s+/).map(Number);
      const inheritedPos = getInheritedProp(className, "pos");
      const pos = explicitPos || inheritedPos || [0, 0, 0];

      // Orientation: explicit format > class-inherited quat > identity
      let quat: number[];
      const hasExplicitOrientationV =
        g.hasAttribute("quat") ||
        g.hasAttribute("euler") ||
        g.hasAttribute("axisangle") ||
        g.hasAttribute("xyaxes") ||
        g.hasAttribute("zaxis");
      if (hasExplicitOrientationV) {
        quat = normalizeQuat(
          parseElementOrientation(g, { eulerseq: parser.eulerseq, angleUnit: parser.angleUnit })
        );
      } else {
        const inheritedQuat = getInheritedProp(className, "quat");
        const raw = inheritedQuat || [1, 0, 0, 0];
        const [w, x, y, z] = raw as number[];
        const mag = Math.sqrt(w * w + x * x + y * y + z * z);
        quat = mag > 0 ? [w / mag, x / mag, y / mag, z / mag] : [1, 0, 0, 0];
      }

      // Get size and type: explicit attribute > inherited from class > defaults
      const type = getGeomType(g, className, "box");
      const size = getGeomSize(g, className, type);
      const hfieldName =
        g.getAttribute("hfield") || (getInheritedProp(className, "hfield") as string | undefined);
      const fromto =
        getFromTo(g) ||
        ((getInheritedProp(className, "fromto") as number[] | undefined)?.slice(0, 6) as
          | Vector6
          | undefined);
      const contactProps = getGeomContactProps(g, className);
      const group = getGeomGroup(g, className);

      const visualGeom = {
        uuid: generateUuid(),
        kind: type,
        size: size,
        position: pos,
        orientation: quat,
        materials: [
          g.getAttribute("material") || getInheritedProp(className, "material") || "default_mat",
        ],
        name: g.getAttribute("name") || "visual",
        visualOnly: true,
        heightField: type === "hfield" ? hfieldName : undefined,
        fromto,
        contactCategory: contactProps.contactCategory,
        contactAffinity: contactProps.contactAffinity,
        group,
        mjcfClass: g.getAttribute("class") || undefined,
        user: g.getAttribute("user") || undefined,
      };

      // console.log(`[MJCFParser] Parsed visual geometry for body ${name}:`, visualGeom);
      return visualGeom;
    });

  // Parse collision geometries (non-mesh geoms classified as collidable)
  // Process in document order by filtering from allGeoms
  const collisionGeoms = allGeoms
    .filter((g: Element) => {
      const className = getGeomClass(g);
      if (getGeomMesh(g, className)) return false;
      return !getVisualOnly(g, className);
    })
    .map((g: Element) => {
      const className = getGeomClass(g);

      // Get position: explicit attribute > inherited from class > default [0,0,0]
      const explicitPos = g.getAttribute("pos")?.trim().split(/\s+/).map(Number);
      const inheritedPos = getInheritedProp(className, "pos");
      const pos = explicitPos || inheritedPos || [0, 0, 0];

      // Orientation: explicit format > class-inherited quat > identity
      let quat: number[];
      const hasExplicitOrientationC =
        g.hasAttribute("quat") ||
        g.hasAttribute("euler") ||
        g.hasAttribute("axisangle") ||
        g.hasAttribute("xyaxes") ||
        g.hasAttribute("zaxis");
      if (hasExplicitOrientationC) {
        quat = normalizeQuat(
          parseElementOrientation(g, { eulerseq: parser.eulerseq, angleUnit: parser.angleUnit })
        );
      } else {
        const inheritedQuat = getInheritedProp(className, "quat");
        const raw = inheritedQuat || [1, 0, 0, 0];
        const [w, x, y, z] = raw as number[];
        const mag = Math.sqrt(w * w + x * x + y * y + z * z);
        quat = mag > 0 ? [w / mag, x / mag, y / mag, z / mag] : [1, 0, 0, 0];
      }

      // Get type: explicit attribute > inherited from geom defaults > inherited from collision type map > infer from size
      let type = g.getAttribute("type") || getInheritedProp(className, "type");

      // If no explicit type, try to get it from class hierarchy (collision type map)
      if (!type && className) {
        type = getCollisionType(className);
      }

      const explicitSize = parseNumericArray(g.getAttribute("size"));
      const inheritedSizeRaw = getInheritedProp(className, "size");
      const inheritedSize = Array.isArray(inheritedSizeRaw) ? inheritedSizeRaw : undefined;
      const baseSize = explicitSize || inheritedSize || [];
      // If still no type, infer from size array
      if (!type) {
        if (baseSize.length === 1) {
          type = "sphere";
        } else if (baseSize.length === 2) {
          type = "capsule";
        } else if (baseSize.length === 3) {
          type = "box";
        } else {
          type = "sphere"; // fallback
        }
      }
      type = String(type).toLowerCase();
      const size = getGeomSize(g, className, String(type));
      const hfieldName =
        g.getAttribute("hfield") || (getInheritedProp(className, "hfield") as string | undefined);
      const fromto =
        getFromTo(g) ||
        ((getInheritedProp(className, "fromto") as number[] | undefined)?.slice(0, 6) as
          | Vector6
          | undefined);
      const contactProps = getGeomContactProps(g, className);
      const group = getGeomGroup(g, className);

      const explicitMass = g.getAttribute("mass");
      const inheritedMass = getInheritedProp(className, "mass");
      const mass = explicitMass
        ? parseFloat(explicitMass)
        : inheritedMass !== undefined
          ? inheritedMass
          : undefined;
      const explicitDensity = g.getAttribute("density");
      const inheritedDensity = getInheritedProp(className, "density");
      const density =
        explicitDensity != null
          ? Number(explicitDensity)
          : inheritedDensity !== undefined
            ? Number(inheritedDensity)
            : undefined;

      const parseFriction = (raw: unknown): Vector3 | undefined => {
        if (!raw) return undefined;
        const nums = Array.isArray(raw)
          ? raw.map((v) => Number(v)).filter((n) => Number.isFinite(n))
          : String(raw)
              .trim()
              .split(/\s+/)
              .map((v) => Number(v))
              .filter((n) => Number.isFinite(n));
        if (!nums.length) return undefined;
        // Normalize to 3 values. If only slide provided, keep our project defaults for the others.
        const slide = nums[0];
        const roll = nums[1] ?? 0.01;
        const spin = nums[2] ?? 0.0001;
        return [slide, roll, spin];
      };

      const frictionCoefficients =
        parseFriction(g.getAttribute("friction")) ||
        parseFriction(getInheritedProp(className, "friction")) ||
        undefined;

      const solverReference = (() => {
        const explicit = parseSolref(g.getAttribute("solref"));
        const inherited = parseSolref(getInheritedProp(className, "solref"));
        return explicit || inherited || ([0.02, 1] as Vector2);
      })();

      const solverImpedance = (() => {
        const explicit = parseSolimp(g.getAttribute("solimp"));
        if (explicit) return explicit;
        const inherited = getInheritedProp(className, "solimp");
        if (Array.isArray(inherited) && inherited.length >= 3) {
          return [
            Number(inherited[0] ?? 0.9),
            Number(inherited[1] ?? 0.95),
            Number(inherited[2] ?? 0.001),
            Number(inherited[3] ?? 0.5),
            Number(inherited[4] ?? 2),
          ] as Vector5;
        }
        return [0.9, 0.95, 0.001, 0.5, 2] as Vector5;
      })();
      const collisionGeom = {
        uuid: generateUuid(),
        kind: type,
        size: size,
        position: pos,
        orientation: quat,
        name: g.getAttribute("name") || "collision",
        materials: [],
        visualOnly: false,
        heightField: type === "hfield" ? hfieldName : undefined,
        fromto,
        contactCategory: contactProps.contactCategory,
        contactAffinity: contactProps.contactAffinity,
        contactDimensions: contactProps.contactDimensions,
        group,
        mjcfClass: g.getAttribute("class") || undefined,
        user: g.getAttribute("user") || undefined,
        mass: Number.isFinite(mass) ? mass : undefined,
        density: Number.isFinite(density) ? density : undefined,
        frictionCoefficients,
        solverReference,
        solverImpedance,
      };

      // console.log(`[MJCFParser] Parsed collision geometry for body ${name}:`, collisionGeom);
      return collisionGeom;
    });

  // Create a map to find parsed geoms by their original XML element
  const geomMap = new Map<Element, ParsedGeom>();
  meshGeoms.forEach((geom, idx) => {
    const meshElems = allGeoms.filter((g) => getGeomMesh(g, getGeomClass(g)));
    if (idx < meshElems.length) {
      geomMap.set(meshElems[idx], geom);
    }
  });
  visualGeoms.forEach((geom, idx) => {
    const visualElems = allGeoms.filter(
      (g) => !getGeomMesh(g, getGeomClass(g)) && getVisualOnly(g, getGeomClass(g))
    );
    if (idx < visualElems.length) {
      geomMap.set(visualElems[idx], geom);
    }
  });
  collisionGeoms.forEach((geom, idx) => {
    const collisionElems = allGeoms.filter((g) => {
      const className = getGeomClass(g);
      if (getGeomMesh(g, className)) return false;
      return !getVisualOnly(g, className);
    });
    if (idx < collisionElems.length) {
      geomMap.set(collisionElems[idx], geom);
    }
  });

  const shouldEmitSyntheticVisualForMeshCollision = (
    geomElem: Element,
    parsedGeom: ParsedGeom
  ): boolean => {
    const className = getGeomClass(geomElem);
    const explicitMaterial = geomElem.getAttribute("material");
    const inheritedMaterial = getInheritedProp(className, "material");
    return shouldEmitSyntheticVisualForMeshCollisionAttrs({
      mesh: parsedGeom.mesh,
      visualOnly: parsedGeom.visualOnly,
      className,
      classParentMap: parser.classParentMap,
      group: parsedGeom.group,
      explicitMaterial,
      inheritedMaterial,
    });
  };

  // Combine geoms in document order (as they appear in XML)
  const geoms: ParsedGeom[] = [];
  allGeoms.forEach((g) => {
    const geom = geomMap.get(g);
    if (geom) {
      if (shouldEmitSyntheticVisualForMeshCollision(g, geom)) {
        geoms.push({
          ...geom,
          uuid: generateUuid(),
          name: geom.name ? `${geom.name}_visual` : undefined,
          visualOnly: true,
          contactCategory: 0,
          contactAffinity: 0,
          contactDimensions: undefined,
          mass: undefined,
          density: undefined,
          frictionCoefficients: undefined,
          solverReference: undefined,
          solverImpedance: undefined,
        });
      }
      warnAmbiguousMeshRole(g, geom);
      geoms.push(geom);
    }
  });

  // Assign user-friendly names to geoms that don't have names
  // Process in document order and assign sequential numbers globally
  geoms.forEach((geom) => {
    if (!geom) return;
    const isVisualOnly = geom.visualOnly === true;
    if (isVisualOnly) {
      // Name default/unnamed visual geoms as visual_X
      if (!geom.name || geom.name === "visual") {
        geom.name = `visual_${parser.globalVisualCounter}`;
        parser.globalVisualCounter++;
      }
    } else {
      // Name unnamed or default collision geoms as geom_X
      if (!geom.name || geom.name === "collision") {
        geom.name = `geom_${parser.globalGeomCounter}`;
        parser.globalGeomCounter++;
      }
    }
  });

  return geoms;
};
